import { BaseUser } from "../../user/base-user.model";
import { QuoteEmail } from "./email.model";
import { Client } from "../../client/client.model";
import { BaseQuote } from "./quote-base.model";
import { QuoteLocation } from "./quote-location.model";
import { LocalCostLabel } from "./local-cost-label.model";
import { Location } from "../../location/location.model";
import { Insurance } from "./insurance.model";

export abstract class BaseStandardQuote extends BaseQuote {
  constructor(
    protected id: number,
    protected displayId: string,
    protected groupId: number,
    protected ref: string,
    protected sender: BaseUser,
    protected createdBy: BaseUser,
    protected status: string,
    protected quoteType: string,
    protected clients: Client[],
    protected createdAt: Date,
    protected updatedAt: Date,
    protected updatedBy: BaseUser,
    protected openedAt: Date,
    protected validFrom: Date,
    protected validTo: Date,
    protected email: QuoteEmail,
    protected internalComment: string,
    protected customTerms: string,
    protected fileName: string,
    protected fileNameInternal: string,
    protected averageMargin: number,
    protected inquiry: string,
    protected source: string,
    protected insurance: Insurance | null,
    protected reasonOfDecline: string,
    protected input: string | null,
    protected offerLanguage: string | null,
    protected hasOrigin: boolean,
    protected hasDestination: boolean,
    protected originLocations: QuoteLocation[],
    protected destinationLocations: QuoteLocation[],
    protected localCostLabels?: LocalCostLabel[],
  ) {
    super(
      id,
      displayId,
      groupId,
      ref,
      sender,
      createdBy,
      status,
      quoteType,
      clients,
      createdAt,
      updatedAt,
      updatedBy,
      openedAt,
      validFrom,
      validTo,
      email,
      internalComment,
      customTerms,
      fileName,
      fileNameInternal,
      averageMargin,
      inquiry,
      source,
      insurance,
      reasonOfDecline,
      input,
    );
  }

  public getHasOrigin(): boolean {
    return this.hasOrigin;
  }

  public setHasOrigin(param: boolean) {
    this.hasOrigin = param;
  }

  public getHasDestination(): boolean {
    return this.hasDestination;
  }

  public setHasDestination(param: boolean) {
    this.hasDestination = param;
  }

  public getOriginLocations(): QuoteLocation[] {
    return this.originLocations;
  }

  public setOriginLocations(value: QuoteLocation[]) {
    this.originLocations = value;
  }

  public getDestinationLocations(): QuoteLocation[] {
    return this.destinationLocations;
  }

  public setDestinationLocations(value: QuoteLocation[]) {
    this.destinationLocations = value;
  }

  public getLocalCostLabels(): LocalCostLabel[] {
    return this.localCostLabels;
  }

  public setLocalCostLabels(value: LocalCostLabel[]) {
    this.localCostLabels = value;
  }

  public getLocalCostLabel(location: Location): string {
    for (let i = 0; i < this.localCostLabels.length; i++) {
      if (this.localCostLabels[i].location.id === location.id) {
        return this.localCostLabels[i].label;
      }
    }

    return null;
  }

  public setLocalCostLabel(location: Location, label: string): void {
    let isAdded = false;
    for (let i = 0; i < this.localCostLabels.length; i++) {
      if (this.localCostLabels[i].location.id === location.id) {
        isAdded = true;
        this.localCostLabels[i].label = label;
      }
    }
    if (!isAdded) {
      this.localCostLabels.push(new LocalCostLabel(label, location));
    }
  }

  public getKind() {
    if (
      this.getOriginLocations().length > 0 ||
      this.getDestinationLocations().length > 0
    ) {
      const location =
        this.getOriginLocations()[0] || this.getDestinationLocations()[0];

      return location.getLocation()?.getKind();
    }
  }

  public isValid(): boolean {
    if (!this.hasOrigin && !this.hasDestination) {
      return false;
    }
    if (this.hasOrigin && !this.originLocations.length) {
      return false;
    }
    if (this.hasDestination && !this.destinationLocations.length) {
      return false;
    }

    return super.isValid();
  }

  // Port-priced quotes hang their rates off a Location; LTL/FTL/PTL price the address itself.
  public requiresResolvedLocation(): boolean {
    return true;
  }

  // A location group leg counts as resolved: it is expanded into its ports server-side.
  public hasResolvedLocations(): boolean {
    if (!this.requiresResolvedLocation()) {
      return true;
    }
    const isResolved = (location: QuoteLocation): boolean =>
      !!location.getLocation() || !!location.getLocationGroup();
    if (this.hasOrigin && !this.originLocations.some(isResolved)) {
      return false;
    }
    if (this.hasDestination && !this.destinationLocations.some(isResolved)) {
      return false;
    }

    return true;
  }

  public getOfferLanguage(): string | null {
    return this.offerLanguage;
  }
  public setOfferLanguage(param: string | null): void {
    this.offerLanguage = param;
  }
}
